import apiClient from './client';
import { postSseStream, readSseFrames, type SseFrame } from './sse';

/** 测试验证中心（测试用例 / 执行报告 / 自由测试会话 / 审批四档）API 客户端。 */

export interface TestCase {
  id: string;
  name: string;
  description: string | null;
  input_text: string;
  expected_output: string | null;
  expected_contains: string[] | null;
  match_type: string;
  tags: string[] | null;
  is_enabled: boolean;
  created_at: string;
}

export interface TestCaseUpsertPayload {
  name: string;
  description?: string;
  input_text: string;
  expected_output?: string;
  expected_contains?: string[];
  match_type: string;
  tags?: string[];
  is_enabled: boolean;
}

export interface TestRun {
  id: string;
  name: string | null;
  status: string;
  total_count: number;
  passed_count: number;
  failed_count: number;
  skipped_count: number;
  pass_rate: number | null;
  duration_ms: number | null;
  created_at: string;
  completed_at: string | null;
}

export interface TestResultDetail {
  id: string;
  sequence: number;
  test_case_id: string;
  test_case_name: string;
  status: string;
  input_text: string;
  actual_output: string | null;
  expected_output: string | null;
  expected_contains: string[] | null;
  matched_keywords: string[] | null;
  missing_keywords: string[] | null;
  similarity_score: number | null;
  duration_ms: number | null;
  error_message: string | null;
}

export interface TestRunReport extends TestRun {
  results: TestResultDetail[];
}

export interface RunTestCasesPayload {
  testcase_ids: string[];
  name: string;
}

/** P10 审批四档 */
export type ApprovalMode = 'allow-all' | 'deny-all' | 'read-only' | 'always-ask';

/** 待人工审批请求（approval_request 消息） */
export interface PendingApproval {
  id: string;
  content: string;
  feedback: {
    status: string;
    operation?: string;
    operation_kind?: string;
    read_only?: boolean;
    diff?: Record<string, unknown>;
    /** B3：问题类别（偏好记忆聚合键，如「外发数据」「删除操作」） */
    category?: string;
  } | null;
}

/** B3 审批偏好：同类问题达到阈值后自动按偏好放行/拒绝 */
export interface ApprovalPreference {
  id: string;
  category: string;
  decision: 'allow' | 'deny';
  hit_count: number;
  threshold: number;
  auto_enabled: boolean;
}

export interface DecideApprovalPayload {
  decision: 'allow' | 'deny';
  /** B3：勾选「记住我的选择」时携带 */
  remember?: boolean;
  category?: string;
}

export interface QuickActionSuggestion {
  id: string;
  kind: 'command' | 'skill';
  payload: {
    label: string;
    explanation: string;
    command?: string;
    instruction?: string;
  };
  status: string;
}

/** 测试会话（POST /agents/{id}/sessions，session_type=testing） */
export interface TestingSession {
  id: string;
  approval_mode?: ApprovalMode | string;
}

/** 插话 / 排队回执（steering_injected / queued_delivered） */
export interface SteerReceipt {
  id: string;
  content: string;
  created_at?: string;
}

/** 流式对话 DONE 帧载荷（与非流式 /chat 返回体对齐） */
export interface ChatDoneFrame {
  usage?: Record<string, unknown> | null;
  message_id?: string | null;
  finish?: string;
  steering_injected?: SteerReceipt[];
  queued_delivered?: SteerReceipt[];
}

export interface ChatStreamHandlers {
  /** TOKEN 帧：逐字追加的回复片段 */
  onToken: (content: string) => void;
  /** REASONING 帧：上游思考片段（可选展示） */
  onReasoning?: (content: string) => void;
  /** DONE 帧：收尾（message_id / 插话与排队回执） */
  onDone: (frame: ChatDoneFrame) => void;
  /** ERROR 帧：中道失败（之后流结束） */
  onError?: (content: string) => void;
}

// ── 测试用例 CRUD ──

export async function listTestCases(agentId: string): Promise<TestCase[]> {
  const res = await apiClient.get(`/agents/${agentId}/testcases`);
  return res.data;
}

export async function createTestCase(agentId: string, payload: TestCaseUpsertPayload): Promise<TestCase> {
  const res = await apiClient.post(`/agents/${agentId}/testcases`, payload);
  return res.data;
}

export async function updateTestCase(
  agentId: string,
  caseId: string,
  payload: TestCaseUpsertPayload
): Promise<TestCase> {
  const res = await apiClient.put(`/agents/${agentId}/testcases/${caseId}`, payload);
  return res.data;
}

export async function deleteTestCase(agentId: string, caseId: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}/testcases/${caseId}`);
}

// ── 测试批次与报告 ──

export async function listTestRuns(agentId: string): Promise<TestRun[]> {
  const res = await apiClient.get(`/agents/${agentId}/testruns`);
  return res.data;
}

export async function runTestCases(agentId: string, payload: RunTestCasesPayload): Promise<TestRunReport> {
  const res = await apiClient.post(`/agents/${agentId}/testruns`, payload);
  return res.data;
}

export async function getTestRun(runId: string): Promise<TestRunReport> {
  const res = await apiClient.get(`/testruns/${runId}`);
  return res.data;
}

// ── 自由测试会话 ──

export async function createTestingSession(agentId: string): Promise<TestingSession> {
  const res = await apiClient.post(`/agents/${agentId}/sessions`, { session_type: 'testing' });
  return res.data;
}

/**
 * 流式对话（SSE）：POST /sessions/{id}/chat/stream。
 *
 * 帧契约：TOKEN 逐字追加、REASONING 思考片段、DONE 收尾（含 steering/queued 回执）、
 * ERROR 中道失败。首帧前失败（上游 4xx/5xx）不走帧，由 postSseStream 抛出 JSON 错误。
 */
export async function streamChatReply(
  sessionId: string,
  payload: { message: string; agent_id?: string },
  handlers: ChatStreamHandlers
): Promise<void> {
  await postSseStream(`/sessions/${sessionId}/chat/stream`, payload, (frame: SseFrame) => {
    const type = frame.type;
    if (type === 'TOKEN') {
      handlers.onToken(String(frame.content ?? ''));
    } else if (type === 'REASONING' && handlers.onReasoning) {
      handlers.onReasoning(String(frame.content ?? ''));
    } else if (type === 'DONE') {
      const payload = { ...frame } as Record<string, unknown>;
      delete payload.type;
      handlers.onDone(payload as unknown as ChatDoneFrame);
    } else if (type === 'ERROR' && handlers.onError) {
      handlers.onError(String(frame.content ?? ''));
    }
  });
}

export async function sendSteerMessage(
  sessionId: string,
  content: string,
  mode: 'steering' | 'queued'
): Promise<void> {
  await apiClient.post(`/sessions/${sessionId}/steer`, { content, mode });
}

// ── P10 审批四档 ──

export async function listPendingApprovals(sessionId: string): Promise<PendingApproval[]> {
  const res = await apiClient.get(`/sessions/${sessionId}/approvals/pending`);
  return res.data;
}

export async function setSessionApprovalMode(sessionId: string, mode: ApprovalMode): Promise<void> {
  await apiClient.patch(`/sessions/${sessionId}/approval-mode`, { approval_mode: mode });
}

export async function decideApproval(
  sessionId: string,
  approvalId: string,
  payload: DecideApprovalPayload
): Promise<void> {
  await apiClient.post(`/sessions/${sessionId}/approvals/${approvalId}/decide`, payload);
}

// ── P3-④ 会话事件流（审批进流）──

/** APPROVAL_REQUEST 帧：审批请求落 pending 后实时推送 */
export interface ApprovalRequestFrame {
  type: 'APPROVAL_REQUEST';
  approval_id: string;
  operation?: string | null;
  operation_kind?: string | null;
  read_only?: boolean | null;
  category?: string | null;
  diff?: Record<string, unknown> | null;
  content?: string;
  created_at?: string;
}

/** APPROVAL_RESOLVED 帧：人工审批决策 commit 后推送 */
export interface ApprovalResolvedFrame {
  type: 'APPROVAL_RESOLVED';
  approval_id: string;
  decision: 'allow' | 'deny';
  decided_at?: string;
}

export interface SessionEventHandlers {
  onApprovalRequest?: (frame: ApprovalRequestFrame) => void;
  onApprovalResolved?: (frame: ApprovalResolvedFrame) => void;
  /** PING 心跳（首帧 + 30s 保活），通常无需处理 */
  onPing?: () => void;
}

/**
 * 订阅会话事件流（SSE）：GET /sessions/{id}/events。
 *
 * 帧契约：APPROVAL_REQUEST（审批进流，含操作上下文与 diff）/
 * APPROVAL_RESOLVED（审批决策落定，含 decision）/ PING（心跳）。
 * 无重放：断连期间的历史由调用方先全量拉 listPendingApprovals 补漏。
 * 端点不可用（404，SESSION_EVENTS_ENABLED=false）或连接失败时抛错，
 * 由调用方回退轮询。
 */
export async function streamSessionEvents(
  sessionId: string,
  handlers: SessionEventHandlers,
  signal?: AbortSignal
): Promise<void> {
  await readSseFrames(`/sessions/${sessionId}/events`, (frame: SseFrame) => {
    if (frame.type === 'APPROVAL_REQUEST') {
      handlers.onApprovalRequest?.(frame as unknown as ApprovalRequestFrame);
    } else if (frame.type === 'APPROVAL_RESOLVED') {
      handlers.onApprovalResolved?.(frame as unknown as ApprovalResolvedFrame);
    } else if (frame.type === 'PING') {
      handlers.onPing?.();
    }
  }, signal);
}

// ── B3 审批偏好记忆 ──

export async function listApprovalPreferences(agentId: string): Promise<ApprovalPreference[]> {
  const res = await apiClient.get(`/agents/${agentId}/approval-preferences`);
  return (res.data?.preferences ?? []) as ApprovalPreference[];
}

export async function revokeApprovalPreference(agentId: string, category: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}/approval-preferences`, { params: { category } });
}

// ── Quick Action 建议（P1） ──

export async function listQuickActionSuggestions(
  agentId: string,
  status = 'pending'
): Promise<QuickActionSuggestion[]> {
  const res = await apiClient.get(`/agents/${agentId}/quick-actions`, { params: { status } });
  return res.data;
}

export async function analyzeSessionQuickActions(
  agentId: string,
  sessionId: string
): Promise<QuickActionSuggestion | null> {
  const res = await apiClient.post(`/agents/${agentId}/quick-actions/analyze`, { session_id: sessionId });
  return (res.data?.suggestion ?? null) as QuickActionSuggestion | null;
}

export async function acceptQuickActionSuggestion(id: string): Promise<void> {
  await apiClient.post(`/quick-actions/${id}/accept`);
}

export async function dismissQuickActionSuggestion(id: string): Promise<void> {
  await apiClient.post(`/quick-actions/${id}/dismiss`);
}
